/**
 * Registro de actividad de "Tu camino inicial" (aceptación de recursos + preguntas al bot).
 *
 * Vive en psiplatform-be (Supabase RW: `PsiJourneyAcceptedResource` / `PsiJourneyBotLog`), NO
 * en api-core — por eso usa `beHttp`, no `secureHttp`. Ver `psiplatform-be/src/modules/journey`.
 *
 * localStorage sigue existiendo como caché instantánea (gatea la UI sin esperar la red, y sirve
 * de fallback si el POST falla), pero la fuente de verdad ya es el backend: `getAcceptedResources`
 * la trae del servidor para hidratar `acceptedKeys`, así sincroniza entre dispositivos.
 */
import { beHttp } from '../beHttp';

const JOURNEY_BASE_PATH = '/api/psychologist/journey';

// ─── Aceptación de recursos ───────────────────────────────────────────────────

function acceptedResourcesKey(psychologistId: string): string {
  return `psi-journey-accepted-resources-${psychologistId}`;
}

export function getAcceptedResourcesLocally(psychologistId: string): Set<string> {
  try {
    const raw = localStorage.getItem(acceptedResourcesKey(psychologistId));
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

/** Se dispara cada vez que se acepta un recurso — la escucha el panel de "artículos
 *  desbloqueados" de la tab Camino Inicial (localStorage no dispara `storage` dentro de la
 *  misma pestaña, así que sin esto no se entera hasta remontar). */
export const JOURNEY_RESOURCE_ACCEPTED_EVENT = 'psi-journey-resource-accepted';

function saveAcceptedResourcesLocally(psychologistId: string, resourceKeys: Iterable<string>): void {
  try {
    localStorage.setItem(acceptedResourcesKey(psychologistId), JSON.stringify(Array.from(new Set(resourceKeys))));
    window.dispatchEvent(new Event(JOURNEY_RESOURCE_ACCEPTED_EVENT));
  } catch { /* noop */ }
}

function saveAcceptedResourceLocally(psychologistId: string, resourceKey: string): void {
  const current = getAcceptedResourcesLocally(psychologistId);
  current.add(resourceKey);
  saveAcceptedResourcesLocally(psychologistId, current);
}

export async function acceptJourneyResource(
  psychologistId: string,
  stage: number,
  resourceKey: string,
): Promise<void> {
  // Guardamos local primero — la UI no puede depender de la latencia de red para gatear.
  saveAcceptedResourceLocally(psychologistId, resourceKey);
  try {
    await beHttp.post(
      `${JOURNEY_BASE_PATH}/accept-resource`,
      { stage, resource_key: resourceKey },
      { showSuccessToast: false, showErrorToast: false },
    );
  } catch {
    // Sin conexión o el backend cayó — ya quedó registrado localmente arriba; se resincroniza
    // cuando `getAcceptedResources` se vuelva a pedir (el server queda como fuente de verdad,
    // pero no perdemos la aceptación local mientras tanto).
  }
}

/**
 * Trae del backend los recursos ya aceptados (todos los dispositivos, no solo este). Actualiza
 * también la copia local para que el próximo mount arranque ya hidratado sin esperar la red.
 * Ante un error de red, devuelve lo que haya en localStorage (mejor mostrar algo que nada).
 */
export async function getAcceptedResources(psychologistId: string): Promise<Set<string>> {
  try {
    const { resource_keys } = await beHttp.get<{ resource_keys: string[] }>(
      `${JOURNEY_BASE_PATH}/accepted-resources`,
      undefined,
      { showSuccessToast: false, showErrorToast: false },
    );
    const merged = new Set(resource_keys.concat(Array.from(getAcceptedResourcesLocally(psychologistId))));
    saveAcceptedResourcesLocally(psychologistId, merged);
    return merged;
  } catch {
    return getAcceptedResourcesLocally(psychologistId);
  }
}

// ─── Preguntas al bot ──────────────────────────────────────────────────────────

interface JourneyBotLogEntry {
  stage: number;
  question: string;
  answer: string;
  created_at: string;
}

function botLogKey(psychologistId: string): string {
  return `psi-journey-bot-log-${psychologistId}`;
}

function saveBotLogLocally(psychologistId: string, entry: JourneyBotLogEntry): void {
  try {
    const stored = JSON.parse(localStorage.getItem(botLogKey(psychologistId)) ?? '[]') as JourneyBotLogEntry[];
    stored.push(entry);
    // Guardamos las últimas 200 nomás — esto es un fallback, no un archivo histórico.
    localStorage.setItem(botLogKey(psychologistId), JSON.stringify(stored.slice(-200)));
  } catch { /* noop */ }
}

export async function logJourneyBotQuestion(
  psychologistId: string,
  stage: number,
  question: string,
  answer: string,
): Promise<void> {
  const entry: JourneyBotLogEntry = { stage, question, answer, created_at: new Date().toISOString() };
  try {
    await beHttp.post(`${JOURNEY_BASE_PATH}/bot-log`, entry, {
      showSuccessToast: false,
      showErrorToast: false,
    });
  } catch {
    saveBotLogLocally(psychologistId, entry);
  }
}
